import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  createConnectAccount,
  getOnboardingLink,
  getAccountStatus,
  refreshOnboardingLink,
  disconnectAccount,
  getStatusMessage
} from '../../services/stripeConnectService';

const BankAccountConnect = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [accountStatus, setAccountStatus] = useState(null);
  const [error, setError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    fetchAccountStatus();
  }, []);

  const fetchAccountStatus = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getAccountStatus();
      setAccountStatus(response.data);
    } catch (err) {
      // If no account exists, that's fine - user hasn't connected yet
      if (err.response?.data?.error?.code === 'NO_CONNECT_ACCOUNT') {
        setAccountStatus({ status: 'not_connected' });
      } else {
        setError(err.response?.data?.error?.message || 'Failed to fetch account status');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleConnectAccount = async () => {
    try {
      setIsProcessing(true);
      setError(null);

      // Create Connect account if it doesn't exist
      if (!accountStatus?.accountId) {
        const createResponse = await createConnectAccount();

        // If account already exists, refresh status and don't continue
        if (createResponse?.data?.error?.code === 'ACCOUNT_EXISTS') {
          await fetchAccountStatus();
          setError('You already have a connected account. Please refresh the page.');
          setIsProcessing(false);
          return;
        }
      }

      // Get onboarding link
      const response = await getOnboardingLink();

      // Redirect to Stripe onboarding
      window.location.href = response.data.url;
    } catch (err) {
      // Handle account already exists error specifically
      if (err.response?.data?.error?.code === 'ACCOUNT_EXISTS') {
        await fetchAccountStatus();
        setError('You already have a connected account. Please refresh the page.');
      } else {
        setError(err.response?.data?.error?.message || 'Failed to start onboarding');
      }
      setIsProcessing(false);
    }
  };

  const handleRefreshLink = async () => {
    try {
      setIsProcessing(true);
      setError(null);

      const response = await refreshOnboardingLink();

      // Redirect to Stripe onboarding
      window.location.href = response.data.url;
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Failed to refresh onboarding link');
      setIsProcessing(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Are you sure you want to disconnect your bank account? You will not be able to receive payouts until you reconnect.')) {
      return;
    }

    try {
      setIsProcessing(true);
      setError(null);

      await disconnectAccount();

      // Refresh status
      await fetchAccountStatus();
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Failed to disconnect account');
    } finally {
      setIsProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600 dark:text-gray-300">Loading account status...</span>
        </div>
      </div>
    );
  }

  const statusInfo = getStatusMessage(accountStatus?.status || 'not_connected');
  const isConnected = accountStatus?.status === 'active';
  const needsAttention = ['pending', 'requires_information', 'verification_pending'].includes(accountStatus?.status);
  const hasAccount = accountStatus?.accountId || accountStatus?.connected;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            Bank Account Connection
          </h2>
          {isConnected && (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
              <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              Connected
            </span>
          )}
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <div className="flex">
              <svg className="w-5 h-5 text-red-600 dark:text-red-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <div className="ml-3">
                <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Status Card */}
        <div className={`p-6 rounded-lg border-2 mb-6 ${
          isConnected
            ? 'bg-green-50 dark:bg-green-900/10 border-green-200 dark:border-green-800'
            : needsAttention
            ? 'bg-yellow-50 dark:bg-yellow-900/10 border-yellow-200 dark:border-yellow-800'
            : 'bg-gray-50 dark:bg-gray-900/30 border-gray-200 dark:border-gray-700'
        }`}>
          <div className="flex items-start">
            <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
              isConnected
                ? 'bg-green-100 dark:bg-green-900/30'
                : needsAttention
                ? 'bg-yellow-100 dark:bg-yellow-900/30'
                : 'bg-gray-100 dark:bg-gray-800'
            }`}>
              {isConnected ? (
                <svg className="w-6 h-6 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : needsAttention ? (
                <svg className="w-6 h-6 text-yellow-600 dark:text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              ) : (
                <svg className="w-6 h-6 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                </svg>
              )}
            </div>
            <div className="ml-4 flex-1">
              <h3 className={`text-lg font-semibold ${
                isConnected
                  ? 'text-green-900 dark:text-green-100'
                  : needsAttention
                  ? 'text-yellow-900 dark:text-yellow-100'
                  : 'text-gray-900 dark:text-gray-100'
              }`}>
                {statusInfo.message}
              </h3>
              <p className={`mt-1 text-sm ${
                isConnected
                  ? 'text-green-700 dark:text-green-300'
                  : needsAttention
                  ? 'text-yellow-700 dark:text-yellow-300'
                  : 'text-gray-600 dark:text-gray-400'
              }`}>
                {statusInfo.action}
              </p>

              {accountStatus?.accountId && (
                <p className="mt-2 text-xs text-gray-500 dark:text-gray-500">
                  Account ID: {accountStatus.accountId}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Information Box */}
        <div className="bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6">
          <div className="flex">
            <svg className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            <div className="ml-3 flex-1">
              <h4 className="text-sm font-medium text-blue-900 dark:text-blue-100">
                About Bank Connection
              </h4>
              <div className="mt-2 text-sm text-blue-800 dark:text-blue-200">
                <ul className="list-disc list-inside space-y-1">
                  <li>Securely connect your UK bank account via Stripe</li>
                  <li>Receive payouts every Monday (funds arrive Wed-Fri)</li>
                  <li>You'll receive 90% of each session payment (10% platform commission)</li>
                  <li>Track your earnings and payout history in real-time</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-3">
          {!hasAccount && !isConnected && !needsAttention && (
            <button
              onClick={handleConnectAccount}
              disabled={isProcessing}
              className="flex-1 sm:flex-initial px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-medium rounded-lg transition-colors duration-200 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {isProcessing ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Connecting...
                </>
              ) : (
                <>
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Connect Bank Account
                </>
              )}
            </button>
          )}

          {needsAttention && (
            <button
              onClick={handleRefreshLink}
              disabled={isProcessing}
              className="flex-1 sm:flex-initial px-6 py-3 bg-yellow-600 hover:bg-yellow-700 disabled:bg-yellow-400 text-white font-medium rounded-lg transition-colors duration-200 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {isProcessing ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Processing...
                </>
              ) : (
                <>
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Complete Verification
                </>
              )}
            </button>
          )}

          {isConnected && (
            <>
              <button
                onClick={handleRefreshLink}
                disabled={isProcessing}
                className="px-6 py-3 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 font-medium rounded-lg transition-colors duration-200 disabled:cursor-not-allowed flex items-center justify-center"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                Manage Account
              </button>
              <button
                onClick={handleDisconnect}
                disabled={isProcessing}
                className="px-6 py-3 bg-red-100 hover:bg-red-200 dark:bg-red-900/30 dark:hover:bg-red-900/50 text-red-700 dark:text-red-300 font-medium rounded-lg transition-colors duration-200 disabled:cursor-not-allowed flex items-center justify-center"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                Disconnect
              </button>
            </>
          )}
        </div>

        {/* Verification Details (if account exists but not fully onboarded) */}
        {accountStatus?.accountId && !isConnected && (
          <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
            <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">
              Verification Checklist
            </h4>
            <div className="space-y-2">
              <div className="flex items-center">
                {accountStatus.detailsSubmitted ? (
                  <svg className="w-5 h-5 text-green-600 dark:text-green-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5 text-gray-400 dark:text-gray-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm0-2a6 6 0 100-12 6 6 0 000 12z" clipRule="evenodd" />
                  </svg>
                )}
                <span className={`text-sm ${accountStatus.detailsSubmitted ? 'text-green-700 dark:text-green-300' : 'text-gray-600 dark:text-gray-400'}`}>
                  Personal details submitted
                </span>
              </div>
              <div className="flex items-center">
                {accountStatus.chargesEnabled ? (
                  <svg className="w-5 h-5 text-green-600 dark:text-green-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5 text-gray-400 dark:text-gray-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm0-2a6 6 0 100-12 6 6 0 000 12z" clipRule="evenodd" />
                  </svg>
                )}
                <span className={`text-sm ${accountStatus.chargesEnabled ? 'text-green-700 dark:text-green-300' : 'text-gray-600 dark:text-gray-400'}`}>
                  Ready to accept payments
                </span>
              </div>
              <div className="flex items-center">
                {accountStatus.payoutsEnabled ? (
                  <svg className="w-5 h-5 text-green-600 dark:text-green-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5 text-gray-400 dark:text-gray-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm0-2a6 6 0 100-12 6 6 0 000 12z" clipRule="evenodd" />
                  </svg>
                )}
                <span className={`text-sm ${accountStatus.payoutsEnabled ? 'text-green-700 dark:text-green-300' : 'text-gray-600 dark:text-gray-400'}`}>
                  Bank account verified for payouts
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BankAccountConnect;
